import { DateTime } from "luxon";
import { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import QueryDataTable from "../shared/QueryDataTable";
import { useRankings, useRankingsMonths } from "../api/rankings";
import ShowIcon from "../shared/ShowIcon";
import { NumberCell } from "./NumberCell";
import { RankCell } from "./RankCell";
import AboutThisReport from "../shared/AboutThisReport";
import AboutRankings from "./AboutRankings";

export const numberFormat = new Intl.NumberFormat().format;
export const deltaFormat = new Intl.NumberFormat(undefined, { signDisplay: "exceptZero" }).format;

export default function Rankings() {
    // TODO: store these in global state!
    const [country, setCountry] = useState("ZZZ");
    const [byCategory, setByCategory] = useState(false);
    const [month, setMonth] = useState();
    const { accountID } = useParams();
    const { data: months = [] } = useRankingsMonths(accountID);
    const query = useRankings(accountID, month),
        { data = [] } = query,
        rankings = data.filter(r => r.countryCode === country);

    useEffect(() => {
        if (months.length && !month) setMonth(months[0]);
    }, [month, months]);

    const ranksort = value => (a, b) =>
        byCategory
            ? a.category === b.category
                ? (value(a) ?? Number.MAX_VALUE) - (value(b) ?? Number.MAX_VALUE)
                : a.category > b.category
                ? 1
                : -1
            : (value(a) ?? Number.MAX_VALUE) - (value(b) ?? Number.MAX_VALUE);

    const config = {
        sheetName: "Podtrac Rankings",
        stickyRows: 1,
        stickyColumns: 1,
        key: data => data.podcastID,
        sortColumn: "UMA Rank",
        sortDirection: "ascending",
        schema: [
            {
                column: "Title",
                type: String,
                value: data => data.title,
                width: 40,
                html: {
                    //column: "Title",
                    component: ({ data, value }) => (
                        <td
                            style={{
                                fontWeight: 600,
                                minWidth: "30ch",
                                maxWidth: "25vw",
                                lineHeight: "2rem"
                            }}
                        >
                            <Link
                                title={value}
                                to={`../shows/${data.podcastID}/rankings`}
                                style={{
                                    display: "flex",
                                    alignItems: "center"
                                }}
                            >
                                <ShowIcon showImageUrl={data.podcastImageUrl} />
                                <span
                                    style={{
                                        maxWidth: "25ch",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}
                                >
                                    {value}
                                </span>
                            </Link>
                        </td>
                    )
                }
            },
            {
                column: "Month",
                type: Date,
                value: data => DateTime.fromISO(data.rankMonth, { zone: "utc" }).toJSDate(),
                width: 15,
                format: "mmm yyyy",
                html: {
                    component: ({ value, data }) => (
                        <td>
                            {DateTime.fromJSDate(value, { zone: "utc" }).toLocaleString({
                                month: "short",
                                year: "numeric"
                            })}
                        </td>
                    )
                }
            },
            {
                column: "Country",
                type: String,
                value: data => (data.countryCode === "ZZZ" ? "Global" : data.countryCode),
                width: 15
            },
            {
                column: "Category",
                type: String,
                value: data => (byCategory ? data.category : "Overall"),
                width: 15
            },
            {
                column: "UMA",
                type: Number,
                value: data => data.listeners,
                width: 15,
                html: {
                    component: NumberCell
                }
            },
            {
                column: "UMA Rank",
                type: Number,
                value: data => (byCategory ? data.categoryListenerRank : data.listenerRank),
                width: 15,
                sort: {
                    ascending: ranksort,
                    descending: value => (a, b) => ranksort(value)(b, a)
                },
                html: {
                    component: ({ data, value }) => (
                        <RankCell
                            rank={value}
                            prevRank={
                                byCategory
                                    ? data.previousCategoryListenerRank
                                    : data.previousListenerRank
                            }
                        />
                    )
                }
            },
            {
                column: "UMA Percentile",
                type: Number,
                value: data =>
                    byCategory ? data.categoryListenerPercentile : data.listenerPercentile,
                width: 15,
                html: {
                    component: NumberCell
                }
            },
            {
                column: "Downloads",
                type: Number,
                value: data => data.downloads,
                width: 15,
                html: {
                    component: NumberCell
                }
            },
            {
                column: "Download Rank",
                type: Number,
                value: data => (byCategory ? data.categoryDownloadRank : data.downloadRank),
                width: 15,
                sort: {
                    ascending: ranksort,
                    descending: value => (a, b) => ranksort(value)(b, a)
                },
                html: {
                    component: ({ data, value }) => (
                        <RankCell
                            rank={value}
                            prevRank={
                                byCategory
                                    ? data.previousCategoryDownloadRank
                                    : data.previousDownloadRank
                            }
                        />
                    )
                }
            },
            {
                column: "Download Percentile",
                type: Number,
                value: data =>
                    byCategory ? data.categoryDownloadPercentile : data.downloadPercentile,
                width: 15,
                html: {
                    component: NumberCell
                }
            }
        ]
    };

    return (
        <>
            <Helmet>
                <title>Rankings - Podtrac</title>
            </Helmet>
            <div style={{ display: "flex", alignItems: "center" }}>
                <h1 style={{ flex: 1 }}>Rankings</h1>
                <AboutThisReport title="Rankings" component={AboutRankings} />
            </div>
            <p style={{ fontSize: "0.75em", margin: "0.25em" }}>
                Compare your shows to all Podtrac-measured podcasts
            </p>

            <QueryDataTable config={config} query={query} filter={r => r.countryCode === country}>
                <select onChange={event => setMonth(event.target.value)}>
                    {months.map(m => (
                        <option key={m} value={m}>
                            {DateTime.fromISO(m, { zone: "utc" }).toLocaleString({
                                month: "short",
                                year: "numeric"
                            })}
                        </option>
                    ))}
                </select>
                <select onChange={event => setCountry(event.target.value)}>
                    <option value="ZZZ">Global</option>
                    <option value="USA">USA</option>
                </select>
                <select onChange={event => setByCategory(!!event.target.value)}>
                    <option value="">Overall</option>
                    <option value="true">By Category</option>
                </select>
            </QueryDataTable>

            {/*<pre>{JSON.stringify(months, null, 2)}</pre>*/}
        </>
    );
}
